"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
  gold: "#D4AF37",         // Gold for stars
};

const testimonials = [
  {
    id: 1,
    name: "Mareshet Kebede Tola",
    role: "Entrepreneur",
    text: "Mareshet is a hardworking entrepreneur in Addis Ababa who runs a small business near the Saris Market, selling handmade home crafts. However, she faced challenges in scaling up her business due to limited working capital. With a 25,000 ETB loan from Yegna Microfinance S.C, Mareshet was able to:Purchase raw materials in bulk at wholesale prices, reducing costs and increasing profitability.Expand her product range to include high-demand handmade crafts and specialty items.Invest in quality packaging materials to enhance branding and attract new customers.Supply shops and mini-markets, participate in business campaigns (like bazaars), and deliver her products beyond Addis Ababa, growing her customer base.",
    videoImage: "/images/about/marshet.png",
    photo: "/images/about/marshet.png",
    rating: 5,
    readTime: 60000,
  },
  {
    id: 2,
    name: "Asamere Eramo Gudiso",
    role: "Entrepreneur",
    text: "Mr. Asamere is a hardworking entrepreneur based in Addis Ababa, specifically in Ledeta Woreda 01, at Sahelu Chaka. He runs a small business that manufactures and sells home furniture and metal products. With a 30,000 ETB loan from Yegna Microfinance S.C., Asamere was able to: Purchase raw materials in bulk at wholesale prices, reducing costs and increasing profitability.Expand his product range to include high-demand furniture, metalwork blends, and specialty items.Invest in packaging materials to improve branding and attract new customers.Grow his customer base by supplying and delivering products both within his local shop area and beyond.",
    videoImage: "/images/about/asmaro2.jpg",
    photo: "/images/about/asmaro2.jpg",
    rating: 5,
    readTime: 65000,
  },
  {
    id: 3,
    name: "Masho and His Friends",
    role: "Entrepreneur",
    text: "Masho and his friends are hardworking entrepreneurs based in Addis Ababa, specifically in Ledeta Woreda 01, at a workplace called Sahelu Chaka. They run a small business that manufactures and sells home furniture, office furniture, and metal products. However, they faced challenges in scaling up due to a lack of working capital. With a 180,000 ETB loan from Yegna Microfinance S.C, Masho and his friends were able to:Purchase raw materials in bulk at wholesale prices, reducing costs and increasing profitability.Expand their product range to include high-demand furniture, metalwork blends, and specialty items.Invest in packaging materials to improve branding and attract new customers.Grow their customer base by supplying and delivering products both within their local shop area and beyond.",
    videoImage: "/images/about/asmaro.png",
    photo: "/images/about/asmaro.png",
    rating: 5,
    readTime: 70000,
  },
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
        setIsAnimating(false);
      }, 1000);
    }, testimonials[activeIndex].readTime);

    return () => clearInterval(interval);
  }, [activeIndex]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        xmlns="http://www.w3.org/2000/svg"
        fill={i < rating ? colors.gold : `${colors.primary}20`}
        viewBox="0 0 24 24"
        width="20"
        height="20"
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L21 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ));
  };

  return (
    <section
      className="relative w-full overflow-hidden py-24 transition-all duration-300"
      style={{
        backgroundColor: colors.white,
        boxShadow: `0px 0px 40px ${colors.primary}20`,
      }}
    >
      <div className="container mx-auto max-w-6xl text-center">
        <h2 className="text-3xl md:text-4xl font-bold" style={{ color: colors.textDark }}>
          Customer <span style={{ color: colors.primary }}>Transformation</span> Story
        </h2>
        <div 
          className="mx-auto mt-4 h-1 w-24 rounded-full"
          style={{ 
            background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})`
          }}
        />
      </div>

      <div className="relative h-auto mt-12 flex flex-col items-center">
        <AnimatePresence mode="wait">
          {testimonials.map(
            (testimonial, index) =>
              activeIndex === index && (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    transition: { 
                      duration: 0.8,
                      ease: [0.16, 1, 0.3, 1] 
                    }
                  }}
                  exit={{ 
                    opacity: 0, 
                    x: -100,
                    transition: { 
                      duration: 0.8,
                      ease: [0.7, 0, 0.84, 0] 
                    }
                  }}
                  className="w-full max-w-5xl p-6 md:p-8 lg:p-10 rounded-2xl flex flex-col md:flex-row gap-6 items-center"
                  style={{
                    backgroundColor: colors.whiteOff,
                    border: `1px solid ${colors.primary}20`,
                    boxShadow: `0 20px 25px -5px ${colors.primary}10, 0 10px 10px -5px ${colors.primary}05`
                  }}
                >
                  <div className="flex flex-col justify-center w-full md:w-2/3 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                      {testimonial.photo && (
                        <motion.img
                          src={testimonial.photo}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                          style={{ border: `2px solid ${colors.primary}` }}
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.3 }}
                        />
                      )}
                      <div>
                        <motion.h3
                          className="text-2xl font-bold"
                          style={{ color: colors.primary }}
                          initial={{ y: 20 }}
                          animate={{ y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          {testimonial.name}
                        </motion.h3>
                        <motion.p 
                          className="text-sm"
                          style={{ color: colors.textDark }}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                        >
                          {testimonial.role}
                        </motion.p>
                      </div>
                    </div>
                    <motion.p 
                      className="text-base leading-relaxed"
                      style={{ color: colors.textDark }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      {testimonial.text}
                    </motion.p>
                    <motion.div 
                      className="flex justify-center md:justify-start gap-2 mt-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 }}
                    >
                      {renderStars(testimonial.rating)}
                    </motion.div>
                  </div>

                  <motion.div 
                    className="w-full md:w-1/3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {testimonial.videoImage && (
                      <img
                        src={testimonial.videoImage}
                        alt="Customer video thumbnail"
                        className="w-full h-55 object-cover rounded-xl shadow-lg"
                        style={{ border: `1px solid ${colors.primary}15` }}
                      />
                    )}
                  </motion.div>
                </motion.div>
              )
          )}
        </AnimatePresence>

        <div className="flex gap-2 mt-6">
          {testimonials.map((_, i) => (
            <motion.div
              key={i}
              className="h-2 rounded-full transition-all duration-500"
              style={{
                backgroundColor: i === activeIndex ? colors.primary : `${colors.primary}20`,
              }}
              animate={{
                width: i === activeIndex ? 32 : 8,
              }}
              transition={{ duration: 0.5 }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;